import { TickerItem, severityConfig } from '../services/serviceHealthTypes';

interface ServiceHealthModalProps {
  item: TickerItem;
  onClose: () => void;
}

export default function ServiceHealthModal({ item, onClose }: ServiceHealthModalProps) {
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const formatTimeAgo = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch {
      return '';
    }
  };

  return (
    <div className="service-health-modal-overlay" onClick={onClose}>
      <div className="service-health-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-row">
            <span className="modal-vendor-icon">{item.vendorIcon}</span>
            <div className="modal-title-info">
              <h2>{item.title}</h2>
              <span className="modal-vendor-name">{item.vendorName}</span>
            </div>
            <span
              className={`modal-severity modal-severity-${item.severity}`}
              style={{
                backgroundColor: severityConfig[item.severity].bgColor,
                color: severityConfig[item.severity].color
              }}
            >
              {severityConfig[item.severity].label}
            </span>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="modal-body">
          <div className="modal-section">
            <div className="modal-meta">
              <div className="modal-meta-item">
                <span className="meta-label">Started</span>
                <span className="meta-value">{formatDate(item.startedAt)}</span>
              </div>
              <div className="modal-meta-item">
                <span className="meta-label">Last Update</span>
                <span className="meta-value">{formatDate(item.updatedAt)} ({formatTimeAgo(item.updatedAt)})</span>
              </div>
              <div className="modal-meta-item">
                <span className="meta-label">Status</span>
                <span className="meta-value">{item.status}</span>
              </div>
            </div>
          </div>

          {item.affectedServices.length > 0 && (
            <div className="modal-section">
              <h3>Affected Services</h3>
              <div className="affected-services">
                {item.affectedServices.map((service, idx) => (
                  <span key={idx} className="service-tag">{service}</span>
                ))}
              </div>
            </div>
          )}

          {item.affectedClients && item.affectedClients.length > 0 && (
            <div className="modal-section">
              <h3>Affected Clients</h3>
              <div className="affected-clients">
                {item.affectedClients.map((client, idx) => (
                  <span key={idx} className="client-tag">{client}</span>
                ))}
              </div>
            </div>
          )}

          {item.updates && item.updates.length > 0 && (
            <div className="modal-section">
              <h3>Update History</h3>
              <div className="update-history">
                {item.updates.map((update, idx) => (
                  <div key={idx} className="update-item">
                    <div className="update-header">
                      <span className="update-status">{update.status}</span>
                      <span className="update-time">{formatDate(update.timestamp)}</span>
                    </div>
                    {update.message && (
                      <p className="update-message">{update.message}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {item.statusPageUrl && (
            <div className="modal-footer">
              <a
                href={item.statusPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="status-page-link"
              >
                View on {item.vendorName} Status Page
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
